/**
 * The Settings screen's stylesheet.
 *
 * NOT part of base-view's `ui/styles/` bundle, and that is the point: that
 * sheet is injected by `startBaseView` and REMOVED on its teardown, while this
 * screen is chrome — it opens from the gear in the top-right toolbar, which
 * outlives the settlement view (a Location visit tears that view down and
 * rebuilds it). A settings panel styled by the view's sheet would lose its
 * skin mid-visit. Injected once per page, like `bootstrap.ts`'s chrome sheet,
 * and never removed.
 *
 * Tokens come from `src/styles/app.css`, which `index.html` loads as a plain
 * global stylesheet that never unmounts — so `var(--brass)` and friends are
 * safe here. (The chrome sheet in `bootstrap.ts` uses literal hex instead;
 * that toolbar is a self-contained visual unit that deliberately does not
 * match the game panels. This screen is a game panel and should match them.)
 *
 * NO BACKTICKS anywhere in this file, comments included: the export is one
 * template literal and a stray backtick terminates it. That has broken this
 * build twice, both times with esbuild pointing ten lines from the cause.
 */
export default `
/* ── The overlay ──────────────────────────────────────────────────────────
   z-index 1250, which is a considered spot rather than a large number:
     1001-1100  game panels (vendor, shop, quest, map, home)
     1002       the player frame, above them so the action bar stays usable
     1200       the tooltip layer
     1250       HERE — settings covers the game entirely, on purpose
     1300       the sign-out confirm, and this screen's own delete confirm,
                which both come from bootstrap.ts's confirmDialog
   Settings is modal over everything in the game and is itself covered only by
   a confirmation. */
.tg-set {
  position: fixed; inset: 0; z-index: 1250;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  background: rgba(6,4,2,0.82);
  backdrop-filter: blur(3px);
  font: 13px/1.5 var(--font-technical);
  color: var(--bone);
}
.tg-set[hidden] { display: none; }

.tg-set-panel {
  position: relative;
  display: grid; grid-template-columns: 186px 1fr;
  width: min(980px, 100%); height: min(660px, 100%);
  background: linear-gradient(165deg, var(--panel-a), var(--panel-b));
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  box-shadow: 0 18px 56px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05);
  overflow: hidden;
}

/* ── The tab rail ─────────────────────────────────────────────────────────
   A rail rather than a row of tabs across the top: there are five sections
   plus an Admin entry, several of them two words, and a horizontal strip
   would either wrap or abbreviate. */
.tg-set-rail {
  display: flex; flex-direction: column; gap: 2px;
  padding: 16px 10px; background: rgba(0,0,0,0.26);
  border-right: 1px solid var(--line);
  overflow-y: auto;
}
.tg-set-brand {
  margin: 0 0 14px; padding: 0 6px;
  font: 700 17px/1.1 var(--font-display); letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--brass-hot);
}
.tg-set-tab {
  display: block; width: 100%; text-align: left; cursor: pointer;
  padding: 8px 10px; border: 1px solid transparent; border-radius: var(--radius-well);
  background: none; color: var(--bone-dim);
  font: 12px var(--font-technical); letter-spacing: 0.1em; text-transform: uppercase;
  transition: color 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}
.tg-set-tab:hover { color: var(--bone); background: rgba(122,90,48,0.18); }
.tg-set-tab-on {
  color: var(--brass-hot); border-color: var(--brass);
  background: linear-gradient(170deg, #3a2717, var(--panel-a));
}
/* The admin entry is a LINK, not a tab — it leaves the game. Separated by a
   rule and pushed to the bottom so it never reads as a sixth section. */
.tg-set-rail-spacer { flex: 1 1 auto; min-height: 12px; }
.tg-set-admin {
  display: block; padding: 8px 10px; text-decoration: none;
  border-top: 1px solid var(--line);
  color: var(--terminal); font: 12px var(--font-technical);
  letter-spacing: 0.1em; text-transform: uppercase;
}
.tg-set-admin:hover { color: #a8f0bd; }

/* ── The content pane ─────────────────────────────────────────────────── */
.tg-set-body { padding: 18px 22px 22px; overflow-y: auto; }

.tg-set-h2 {
  margin: 0 0 14px;
  font: 700 22px/1 var(--font-display); letter-spacing: 0.04em;
  text-transform: uppercase; color: var(--brass-hot);
}
.tg-set-section { margin-bottom: 22px; }
/* Janeks: "no need for full width inputs". The pane is ~700px wide and a text
   field stretched across all of it is both ugly and harder to read — the eye
   loses the line. Each section caps its own measure instead of the inputs
   doing it, so the heading, hint and actions stay aligned with the fields.
   The -narrow suffix is the two-column sections, -col the single-column
   ones. NO BACKTICKS in this file, comments included -- see the header. */
.tg-set-section-narrow { max-width: 520px; }
/* Account beside Preferences. align-items: start so the shorter of the two
   does not stretch its background down to match the taller. */
.tg-set-cols {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 26px; align-items: start; max-width: 660px;
}
.tg-set-cols > .tg-set-section { max-width: none; }
.tg-set-section-col { max-width: 340px; }
.tg-set-h3 {
  margin: 0 0 9px;
  font: 700 13px/1 var(--font-display); letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--brass-bright);
}
.tg-set-hint {
  margin: -4px 0 10px; color: var(--bone-faint);
  font: 11px/1.45 var(--font-technical); letter-spacing: 0.04em;
}

/* ── Forms ────────────────────────────────────────────────────────────── */
/* Billing: core's gateway rules, inline. #ff9f7a on the panel is AA. */
.tg-set-field-err { margin: 3px 0 0; color: #ff9f7a; font: 11px/1.4 var(--font-technical); letter-spacing: 0.03em; }
.tg-set-field-err[hidden] { display: none; }
.tg-set-input[aria-invalid="true"] { border-color: var(--rust); }
.tg-set-billing-need { color: var(--warn); }
.tg-set-billing-ok { color: var(--bone-faint); }
.tg-set-grid { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px 14px; }
.tg-set-grid-1 { grid-template-columns: 1fr; }
.tg-set-grid-3 { grid-template-columns: repeat(3, minmax(0,1fr)); }
/* One field that should not run the width of its row. */
.tg-set-grid-tight { max-width: 260px; }
/* Two short fields side by side INSIDE a one-column grid (flat/floor,
   post code/city) — a nested grid rather than column spans, so the address
   reads as a stack of lines with two of them split. */
.tg-set-pair { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px 14px; }
.tg-set-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.tg-set-field-wide { grid-column: 1 / -1; }
.tg-set-label {
  color: var(--bone-dim); font: 10px var(--font-technical);
  letter-spacing: 0.14em; text-transform: uppercase;
}
.tg-set-input, .tg-set-select {
  width: 100%; box-sizing: border-box; padding: 7px 9px;
  background: var(--well); color: var(--bone);
  border: 1px solid var(--line); border-radius: var(--radius-well);
  font: 13px var(--font-technical);
}
.tg-set-input:focus, .tg-set-select:focus {
  outline: none; border-color: var(--brass); color: var(--bone);
}
.tg-set-input:disabled, .tg-set-select:disabled { color: var(--bone-faint); cursor: not-allowed; }
.tg-set-input[readonly] { color: var(--bone-dim); cursor: default; }
/* The native arrow is drawn in the OS colour and reads as a light smudge on
   this palette. Hidden, and replaced with one drawn in brass. */
.tg-set-select {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--brass) 50%),
                    linear-gradient(135deg, var(--brass) 50%, transparent 50%);
  background-position: right 14px center, right 9px center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 26px;
}
.tg-set-select option { background: #14100b; color: var(--bone); }

.tg-set-actions { display: flex; gap: 8px; align-items: center; margin-top: 12px; }
.tg-set-btn {
  padding: 8px 16px; cursor: pointer;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: rgba(122,90,48,0.3); color: var(--bone);
  font: 700 11px var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}
.tg-set-btn:hover:not(:disabled) { background: rgba(122,90,48,0.55); color: var(--bone); }
.tg-set-btn:disabled { opacity: 0.45; cursor: default; }
.tg-set-btn-go {
  background: linear-gradient(160deg, var(--brass-hot), var(--brass));
  border-color: var(--brass); color: #1a1208;
}
.tg-set-btn-go:hover:not(:disabled) { background: linear-gradient(160deg, #ffd08a, var(--brass-hot)); }
.tg-set-btn-danger {
  background: linear-gradient(180deg, #4a1712, #250b08);
  border-color: #7e2b1f; color: #f0a893;
}
.tg-set-btn-danger:hover:not(:disabled) { color: #ffd3c4; border-color: var(--rust); }

/* ── Messages ─────────────────────────────────────────────────────────── */
.tg-set-msg {
  margin: 0 0 12px; padding: 8px 11px; border-radius: var(--radius-well);
  font: 12px var(--font-technical); letter-spacing: 0.03em;
}
.tg-set-msg-err {
  position: relative; overflow: hidden; color: #ffdccf; padding-left: 16px;
  border: 2px solid #1a0a07; text-shadow: 0 1px 0 #000;
  background:
    repeating-linear-gradient(135deg, #17100a 0 6px, #c9902c 6px 12px) left top / 8px 100% no-repeat,
    linear-gradient(180deg, rgba(190,90,36,0.35), transparent) 40% 0 / 12px 100% no-repeat,
    linear-gradient(112deg, transparent 38%, rgba(255,225,200,0.12) 38.4%, transparent 39.2%),
    radial-gradient(circle at 14% 30%, rgba(255,235,220,0.1) 0 1px, transparent 1.6px) 0 0 / 11px 8px,
    linear-gradient(180deg, #632a1b, #260d08);
  box-shadow: inset 0 1px 0 rgba(255,190,160,0.25), inset 0 -2px 0 rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.55);
}
@keyframes tgset-err-in {
  0% { opacity: 0; transform: translateX(-16px); filter: brightness(2.2); }
  18% { opacity: 1; transform: translateX(8px); }
  36% { transform: translateX(-6px); }
  54% { transform: translateX(3px); }
  100% { opacity: 1; transform: none; filter: none; }
}
.tg-set-msg-err.bv-err-in { animation: tgset-err-in 0.55s cubic-bezier(0.2, 0.8, 0.3, 1) both; }
@media (prefers-reduced-motion: reduce) { .tg-set-msg-err.bv-err-in { animation: none; } }
.tg-set-msg-ok { color: #cbe0a4; background: rgba(116,133,74,0.16); border: 1px solid rgba(116,133,74,0.5); }

/* ── Profile: the survivor card ─────────────────────────────────────────
   Janeks: "Make it a card, style it more, add border to the player preview,
   show the level as a ribbon or a small card ... when i hover on the player
   card, card itself lifts up. Use depth and animate based on cursor position
   in the card."

   Three things make the depth read, and all three are needed:
     1. perspective on the CARD, so the tilt is a real 3D rotation of the
        inner plate rather than a skew. The perspective lives here and not on
        the pane, because a shared perspective origin makes an off-centre card
        look sheared even at rest.
     2. a shadow that GROWS on hover. A tilt with a fixed shadow reads as the
        card warping, not lifting.
     3. a glare that follows the pointer (--gx/--gy from mountProfileCard).
        Without it the lit side does not change as the card turns, which is
        what makes a CSS tilt feel like paper instead of a surface.

   --tilt-x/--tilt-y/--gx/--gy are written by mountProfileCard on pointermove
   and reset on leave. Declared here with rest values so the card renders
   correctly before the first move, and so a browser that never fires
   pointermove gets a flat, correct card rather than an invalid transform. */
.tg-set-card {
  --tilt-x: 0deg; --tilt-y: 0deg; --gx: 50%; --gy: 0%;
  position: relative; max-width: 500px;
  /* Reserves the glow's room inside .tg-set-body's padding — see the hover
     rule below for why the shadow would otherwise be clipped on three sides.
     The bottom margin is the section gap plus the same allowance. */
  margin: 4px 10px 30px;
  perspective: 900px;
  /* The tilt is applied to .tg-set-card-inner; this element stays flat so the
     hover target does not move out from under the pointer as it rotates,
     which would make the tilt oscillate. */
}
.tg-set-card-inner {
  position: relative;
  display: grid; grid-template-columns: 116px 1fr; gap: 16px;
  padding: 16px 18px;
  background:
    repeating-linear-gradient(112deg, rgba(0,0,0,0.13) 0 2px, rgba(0,0,0,0) 2px 13px),
    linear-gradient(158deg, #33281a 0%, var(--panel-a) 46%, #17110b 100%);
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  box-shadow:
    0 6px 16px rgba(0,0,0,0.5),
    inset 0 1px 0 rgba(255,255,255,0.055),
    inset 0 -18px 30px rgba(0,0,0,0.3);
  transform: rotateX(var(--tilt-x)) rotateY(var(--tilt-y));
  transform-style: preserve-3d;
  /* Fast on the tilt so it tracks the cursor, slower on the lift so the card
     settles rather than snapping. A single transition on both would make the
     tilt feel like it was dragging behind the pointer. */
  transition: transform 0.09s ease-out, box-shadow 0.22s ease, border-color 0.22s ease;
  overflow: hidden;
}
/* Janeks: "The glow effect is cut by the container. Should go outside. Can
   also make the glow effect lighter."
   
   The clipper is NOT this card's own overflow — it is .tg-set-body, whose
   overflow-y: auto makes it a clipping box on BOTH axes (per spec, a
   visible on the other axis computes to auto), and the body's 22px of
   padding was less than the old 46px blur. Two changes, because either alone
   leaves it cut: the glow is lighter and tighter (18px blur, and brass at 0.13
   rather than 0.22), and .tg-set-card carries a margin that reserves room
   for it inside that padding. */
.tg-set-card:hover .tg-set-card-inner {
  border-color: var(--brass);
  box-shadow:
    0 12px 26px rgba(0,0,0,0.5),
    0 0 18px rgba(220,176,106,0.13),
    0 0 0 1px rgba(220,176,106,0.16),
    inset 0 1px 0 rgba(255,255,255,0.07),
    inset 0 -18px 30px rgba(0,0,0,0.24);
}
/* The lift itself — a translate on the PARENT, so it composes with the
   child's rotation instead of being overwritten by it. */
.tg-set-card { transition: transform 0.22s ease; transform: translateY(0); }
.tg-set-card:hover { transform: translateY(-5px); }

/* Pointer-follow sheen. pointer-events: none is load-bearing: this covers the
   whole card, and without it the pointermove handler would still fire (it is
   on the card) but :hover on the portrait and the ribbon would not. */
.tg-set-card-glare {
  position: absolute; inset: 0; border-radius: var(--radius-panel);
  pointer-events: none; opacity: 0; z-index: 2;
  background: radial-gradient(240px 180px at var(--gx) var(--gy),
    rgba(255,214,150,0.15), rgba(255,214,150,0) 68%);
  transition: opacity 0.22s ease;
}
.tg-set-card:hover .tg-set-card-glare { opacity: 1; }

/* The preview's frame — the border Janeks asked for, doubled: a brass hairline
   inside a dark bezel, which is what makes it read as a mounted photograph
   rather than a div with a 1px outline. */
.tg-set-card-frame {
  position: relative; padding: 4px;
  background: linear-gradient(160deg, #4a3620, #1d150d);
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  box-shadow: 0 2px 8px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(0,0,0,0.6);
}

.tg-set-card-body { display: flex; flex-direction: column; min-width: 0; padding-right: 58px; }
.tg-set-card-kicker {
  margin: 0 0 3px; color: var(--brass);
  font: 9px var(--font-technical); letter-spacing: 0.22em; text-transform: uppercase;
}
/* The two dated facts, as a description list — they are label/value pairs and
   the markup should say so. Was three stacked <p> that all looked alike. */
.tg-set-card-facts {
  margin: 12px 0 0; display: flex; flex-wrap: wrap; gap: 4px 22px;
}
.tg-set-card-facts div { display: flex; flex-direction: column; gap: 2px; }
.tg-set-card-facts dt {
  color: var(--bone-faint); font: 9px var(--font-technical);
  letter-spacing: 0.18em; text-transform: uppercase;
}
.tg-set-card-facts dd {
  margin: 0; color: var(--bone-dim); font: 12px var(--font-technical);
  letter-spacing: 0.06em;
}

/* The level, as a ribbon pinned to the card's top-right corner.
   Absolute inside .tg-set-card-inner (which is the positioned ancestor and
   also the rotated one), so it tilts WITH the card instead of floating over
   a turning plate. translateZ lifts it off that plate by 10px, which is the
   only reason the tilt makes it look attached rather than printed on. */
.tg-set-ribbon {
  position: absolute; top: 0; right: 16px; z-index: 4;
  display: flex; flex-direction: column; align-items: center; gap: 1px;
  min-width: 44px; padding: 7px 6px 10px;
  background: linear-gradient(170deg, var(--brass-hot), #8e6a2c);
  color: #1a1208;
  clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 82%, 0 100%);
  box-shadow: 0 5px 12px rgba(0,0,0,0.5);
  transform: translateZ(10px);
}
.tg-set-ribbon-k {
  font: 700 8px/1 var(--font-technical); letter-spacing: 0.16em; text-transform: uppercase;
  opacity: 0.8;
}
.tg-set-ribbon-n { font: 700 21px/1 var(--font-display); letter-spacing: 0.02em; }
/* No survivor yet: the same ribbon in dead grey, so the shape stays constant
   and only the state reads differently. */
.tg-set-ribbon-none {
  background: linear-gradient(170deg, #4a4132, #2a251c); color: var(--bone-faint);
  padding: 8px 7px 12px;
}
.tg-set-ribbon-none span {
  font: 700 9px/1.25 var(--font-technical); letter-spacing: 0.1em;
  text-transform: uppercase; text-align: center;
}

/* Kept: the reduced-motion answer. The lift, the tilt and the glare are all
   decoration, and a player who asked the OS for less movement should get a
   card that simply sits there. The custom properties keep being written —
   stopping that would mean a second code path — but nothing reads them. */
@media (prefers-reduced-motion: reduce) {
  .tg-set-card, .tg-set-card:hover { transform: none; }
  .tg-set-card-inner { transform: none; transition: box-shadow 0.22s ease, border-color 0.22s ease; }
  .tg-set-card-glare { display: none; }
}

/* position: relative is LOAD-BEARING. CharacterPreview appends a canvas that
   is absolute; inset: 0, so an unpositioned host lets it size against the
   nearest positioned ancestor — which here is the full-screen overlay. That
   exact bug shipped twice (the arena confirm, then the home panel); the
   component now enforces it too, and this is the belt to that braces. */
.tg-set-portrait {
  position: relative; touch-action: none;
  width: 108px; height: 150px; overflow: hidden;
  border: 1px solid rgba(0,0,0,0.75); border-radius: 3px;
  background: radial-gradient(115% 85% at 50% 15%, #3a281a, var(--well));
  box-shadow: inset 0 0 14px rgba(0,0,0,0.55);
}
.tg-set-who-name {
  margin: 0; font: 700 20px/1.1 var(--font-display);
  letter-spacing: 0.04em; color: var(--bone);
}
.tg-set-badge {
  display: inline-block; margin-left: 7px; padding: 1px 6px;
  border-radius: var(--radius-tape); font-size: 10px; letter-spacing: 0.1em;
}
.tg-set-badge-ok { color: var(--moss); border: 1px solid var(--moss-dark); }
.tg-set-badge-warn { color: var(--warn); border: 1px solid var(--warn); }

/* ── Danger zone ──────────────────────────────────────────────────────── */
.tg-set-danger {
  padding: 13px 15px; border-radius: var(--radius-well);
  border: 1px solid #7e2b1f; background: rgba(74,23,18,0.22);
}
.tg-set-danger .tg-set-h3 { color: #f0a893; }

/* ── Sound ────────────────────────────────────────────────────────────── */
.tg-set-mix { display: flex; flex-direction: column; gap: 14px; max-width: 460px; }
.tg-set-chan { display: grid; grid-template-columns: 74px 1fr 46px 62px; gap: 10px; align-items: center; }
.tg-set-chan-name { color: var(--bone); font: 12px var(--font-technical); letter-spacing: 0.08em; text-transform: uppercase; }
.tg-set-chan-hint { grid-column: 2 / -1; margin: -6px 0 0; color: var(--bone-faint); font-size: 10px; }
.tg-set-chan-val { color: var(--brass-bright); font-variant-numeric: tabular-nums; text-align: right; font-size: 12px; }
/* A muted channel's slider stays usable — the level is still meaningful, and
   graying the control out would make unmuting feel like it lost the value. */
.tg-set-chan-off .tg-set-chan-name, .tg-set-chan-off .tg-set-chan-val { color: var(--bone-faint); }
.tg-set-range { width: 100%; accent-color: var(--brass-hot); cursor: pointer; }
.tg-set-mute {
  padding: 4px 6px; cursor: pointer; border-radius: var(--radius-tape);
  border: 1px solid var(--line); background: var(--well); color: var(--bone-dim);
  font: 10px var(--font-technical); letter-spacing: 0.08em; text-transform: uppercase;
}
.tg-set-mute-on { color: var(--rust); border-color: var(--rust); }

/* ── The Controls tab ──────────────────────────────────────────────────────
   A reference card first, a settings page second: half the reported problem
   was not knowing what the buttons were. The list is a <dl> because that is
   what it is — bindings and what they do. */
.tg-set-keylist {
  display: grid; grid-template-columns: max-content 1fr; gap: 7px 16px;
  margin: 0 0 18px; align-items: baseline;
}
.tg-set-keylist dt { color: var(--bone-dim); font: 11px var(--font-technical); letter-spacing: 0.04em; }
/* Same key cap the walkthrough draws (styles/tutorial.css.ts) — the two are
   the only places key names are printed, and they must read as one game.
   NO BACKTICKS in this file, comments included: this export is one template
   literal and a stray backtick terminates it. */
.tg-set-keylist kbd {
  display: inline-block; padding: 1px 6px; margin: 0 1px;
  background: var(--well); border: 1px solid var(--line); border-radius: var(--radius-tape);
  color: var(--brass-bright); font: 11px var(--font-technical); letter-spacing: 0.06em;
}
.tg-set-keylist dd { margin: 0; color: var(--bone); font-size: 12px; }
.tg-set-key-off { color: var(--bone-faint); font-style: italic; }
.tg-set-opt { display: flex; gap: 12px; align-items: flex-start; padding-top: 14px; border-top: 1px solid var(--line); }
.tg-set-opt-name { margin: 0 0 3px; color: var(--bone); font-size: 12px; }
.tg-set-opt-hint { margin: 0; color: var(--bone-faint); font-size: 10px; line-height: 1.5; max-width: 46ch; }
/* Wide enough that On and Off do not change the layout as it is toggled. */
.tg-set-toggle {
  flex: none; min-width: 58px; padding: 5px 10px; cursor: pointer;
  border-radius: var(--radius-tape); border: 1px solid var(--line);
  background: var(--well); color: var(--bone-dim);
  font: 10px var(--font-technical); letter-spacing: 0.08em; text-transform: uppercase;
}
.tg-set-toggle-on { color: var(--moss); border-color: var(--moss); }

/* ── The Tutorial tab's one card ───────────────────────────────────────────
   Horizontal: icon, prose, button, in a row. The button is vertically centred
   rather than aligned to the top, because the prose is three lines and a
   top-aligned CTA in a row that tall reads as belonging to the heading. */
.tg-set-cta {
  display: grid; grid-template-columns: 52px 1fr auto; gap: 16px;
  align-items: center; max-width: 640px; padding: 18px 20px;
  background:
    repeating-linear-gradient(112deg, rgba(0,0,0,0.12) 0 2px, rgba(0,0,0,0) 2px 13px),
    linear-gradient(158deg, #33281a, var(--panel-a) 52%, #17110b);
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  box-shadow: 0 6px 18px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
}
.tg-set-cta-icon {
  display: flex; align-items: center; justify-content: center;
  width: 52px; height: 52px; border-radius: 50%;
  background: radial-gradient(circle at 38% 30%, var(--brass-hot), #7a5a30);
  color: #1a1208; font: 700 26px/1 var(--font-display);
  box-shadow: 0 3px 10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.3);
}
.tg-set-cta-text { min-width: 0; }
.tg-set-cta-text .tg-set-h3 { margin-bottom: 5px; }
.tg-set-cta-body { margin: 0; color: var(--bone-dim); font: 12px/1.6 var(--font-body); }
.tg-set-cta-btn { white-space: nowrap; align-self: center; }

/* ── Tables (Invoices, Ledger) ────────────────────────────────────────── */
.tg-set-table { width: 100%; border-collapse: collapse; font: 12px var(--font-technical); }
.tg-set-table th {
  text-align: left; padding: 6px 8px; color: var(--brass-bright);
  font-weight: 400; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  border-bottom: 1px solid var(--line);
}
.tg-set-table td {
  padding: 7px 8px; border-bottom: 1px solid rgba(60,48,31,0.5);
  color: var(--bone); vertical-align: top;
}
.tg-set-table td.tg-set-num { text-align: right; font-variant-numeric: tabular-nums; }
.tg-set-gold { color: var(--brass-hot); }
.tg-set-num { white-space: nowrap; }
.tg-set-link { color: var(--brass); text-decoration: none; }
.tg-set-link:hover { color: var(--brass-hot); text-decoration: underline; }
.tg-set-withdraw { max-width: 480px; }
.tg-set-num .bv-ccoin, .tg-set-coin { vertical-align: middle; margin-right: 4px; }
.tg-set-coin { width: 16px; height: 16px; object-fit: contain; }
/* The unit stays for screen readers; the coin carries it visually. */
.tg-set-unit { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.tg-set-cash { color: var(--bone-dim); }
.tg-set-debit { color: #e0a08c; }
.tg-set-credit { color: var(--moss); }
.tg-set-empty { color: var(--bone-faint); font: 12px var(--font-note); padding: 14px 0; }
/* The stand-in row Invoices and Ledger show when they have nothing to list.
   Centred and unruled — it is not a record, and a bottom border would read as
   the first of several rows. */
.tg-set-row-empty td {
  text-align: center; padding: 26px 8px; border-bottom: 0;
  color: var(--bone-faint); font: 12px var(--font-note);
}
/* A table is the one thing here that can be wider than the pane. It scrolls
   inside its own box rather than pushing the panel sideways. */
.tg-set-scroll { overflow-x: auto; }

/* ── Legal ────────────────────────────────────────────────────────────── */
.tg-set-docs { display: flex; flex-direction: column; gap: 7px; max-width: 420px; }
.tg-set-doc {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 12px; min-height: 44px; cursor: pointer; text-align: left;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: var(--well); color: var(--bone); text-decoration: none;
  font: 13px var(--font-technical); letter-spacing: 0.04em;
}
.tg-set-doc:hover { border-color: var(--brass); color: var(--brass-hot); }
.tg-set-doc-arrow { color: var(--brass); }

@media (max-width: 720px) {
  .tg-set-panel { grid-template-columns: 1fr; height: 100%; }
  .tg-set-rail { flex-direction: row; flex-wrap: wrap; border-right: 0; border-bottom: 1px solid var(--line); }
  .tg-set-brand { width: 100%; margin-bottom: 8px; }
  .tg-set-rail-spacer { display: none; }
  .tg-set-admin { border-top: 0; }
  .tg-set-grid, .tg-set-grid-3, .tg-set-pair { grid-template-columns: 1fr; }
  /* The card stacks, and drops its effects: a tilt on a card the width of the
     screen is nausea, not depth, and the ribbon has no corner to pin to. */
  .tg-set-card-inner { grid-template-columns: 1fr; transform: none; }
  .tg-set-card, .tg-set-card:hover { transform: none; }
  .tg-set-card-glare { display: none; }
  .tg-set-card-body { padding-right: 0; }
  .tg-set-card-frame { width: 116px; }
  .tg-set-ribbon { transform: none; }
  /* Sections stop capping their measure — there is no width left to spare. */
  .tg-set-section-narrow, .tg-set-section-col, .tg-set-grid-tight { max-width: none; }
  /* The CTA stacks: icon and heading on one row is not worth the columns at
     this width, and a nowrap button beside three lines of prose overflows. */
  .tg-set-cta { grid-template-columns: 52px 1fr; }
  .tg-set-cta-btn { grid-column: 1 / -1; }
}
`;
